/*
  <newsletter-popup> — opens an email capture dialog once the reader has scrolled
  past a share of the page.

  Native <dialog> + showModal() is doing the heavy lifting: focus trap, Esc to close,
  background made inert, and ::backdrop. Everything here is just the decision of *when*
  to open it and whether it has been dismissed before.

  Suppression is intentionally generous, because a popup that reappears is the whole
  reason people hate popups:
    - dismissed  -> hidden for `data-remember-days`
    - subscribed -> hidden permanently
    - one open per page load, no re-trigger on scrolling back
*/

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    this.key = this.dataset.storageKey || 'newsletter-popup';
    this.threshold = Math.min(Math.max(parseInt(this.dataset.scrollThreshold, 10) || 50, 1), 99) / 100;
    this.rememberDays = parseInt(this.dataset.rememberDays, 10) || 30;
    this.opened = false;

    this.querySelector('[data-newsletter-popup-close]')
      ?.addEventListener('click', () => this.close('dismissed'));

    // Clicking the backdrop closes. <dialog> reports clicks on the backdrop as clicks
    // on the dialog itself, so compare against its box rather than the event target.
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const r = this.dialog.getBoundingClientRect();
      const outside =
        event.clientX < r.left || event.clientX > r.right ||
        event.clientY < r.top || event.clientY > r.bottom;
      if (outside) this.close('dismissed');
    });

    // Esc fires `cancel`; treat it as a dismissal so it is remembered.
    this.dialog.addEventListener('cancel', () => this.remember('dismissed'));

    // Returning from a successful POST — Liquid rendered the success message instead of
    // the form, so its presence is the signal. Reopen into it and never show it again.
    if (this.querySelector('.newsletter-popup__success')) {
      this.open();
      this.remember('subscribed', true);
      return;
    }

    if (this.suppressed()) return;

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  onScroll() {
    if (this.opened) return;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    // A page too short to scroll can never reach the threshold; don't trap the reader
    // in a popup they never asked for by dividing by zero.
    if (scrollable <= 0) return;
    if (window.scrollY / scrollable >= this.threshold) this.open();
  }

  open() {
    if (this.opened) return;
    this.opened = true;
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    this.lastFocus = document.activeElement;
    this.dialog.showModal();
    this.querySelector('.newsletter-popup__input')?.focus({ preventScroll: true });
  }

  close(reason) {
    this.remember(reason);
    this.dialog.close();
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus({ preventScroll: true });
    }
  }

  remember(reason, forever = false) {
    try {
      const until = forever ? 0 : Date.now() + this.rememberDays * 864e5;
      localStorage.setItem(this.key, JSON.stringify({ reason, until, forever }));
    } catch (e) {
      /* private mode or blocked storage — the popup simply shows again next visit */
    }
  }

  suppressed() {
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return false;
      const v = JSON.parse(raw);
      return v.forever === true || (typeof v.until === 'number' && Date.now() < v.until);
    } catch (e) {
      return false;
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
